import {
  hasPlaceholderVisitor,
  readPlaceholderVisitorName,
} from './placeholderVisitorKeys'

/**
 * A game as the app sees it -- either raw from the API or after
 * `common/reducers/parsers.js`, which is why both name spellings are accepted.
 */
export interface VisitingTeamDisplaySource {
  visitingTeam?: {
    id?: string | null
    name?: string | null
    fullName?: string | null
    name_full?: string | null
    imageId?: string | null
  } | null
  visitingTeamId?: string | null
  /** Bracket slot ("Winner of SF1") -- a tier of its own, above the placeholder. */
  visitingTeamSlot?: {
    name?: string | null
    name_full?: string | null
  } | null
  /*
   * Optional rather than an index signature. `[key: string]: unknown` forced
   * every caller's game type to carry one too, which web's typed `Game`
   * does not -- passing it was a type error even though the shape is right.
   * The placeholder keys are read through `PlaceholderVisitorSource`, which
   * keeps its own index signature for the raw/parsed spellings.
   */
  has_placeholder_visitor?: unknown
  hasPlaceholderVisitor?: unknown
  visiting_team_placeholder_name?: unknown
  visitingTeamPlaceholderName?: unknown
}

export interface VisitingTeamDisplay {
  /** Name to render. Null only when the game has neither a team nor a placeholder. */
  name: string | null
  /** Team image id, or null -- callers pair this with their placeholder asset. */
  imageId: string | null
  /** Team id for navigation/roster lookups; null for a placeholder visitor. */
  teamId: string | null
  /** True when this is an admin-typed name rather than a real team. */
  isPlaceholder: boolean
  /** False when there is nothing to navigate to -- a placeholder has no team page. */
  canNavigate: boolean
}

/**
 * One answer to "what do I show for the visiting side?".
 *
 * Screens call this instead of reaching into `game.visitingTeam` themselves,
 * so the placeholder case is decided once. Logos are intentionally left as a
 * null `imageId`: every call site already passes its own placeholder asset
 * (generic vs. soccer), and choosing one here would override that.
 */
export const resolveVisitingTeamDisplay = (
  game: VisitingTeamDisplaySource | null | undefined
): VisitingTeamDisplay => {
  const team = game?.visitingTeam
  const teamId = team?.id ?? game?.visitingTeamId ?? null
  const realTeamName = team?.fullName ?? team?.name_full ?? team?.name ?? null

  /*
   * Name resolution order, per the SN-8410 contract:
   *   real team -> tournament slot -> placeholder.
   *
   * The slot tier matters because a bracket game ("Winner of SF1") also has
   * no visiting team id. Dropping straight to the placeholder would blank a
   * slot name that is already there to be shown.
   */
  const slotName =
    game?.visitingTeamSlot?.name_full ?? game?.visitingTeamSlot?.name ?? null

  if (hasPlaceholderVisitor(game)) {
    return {
      name: realTeamName ?? slotName ?? readPlaceholderVisitorName(game),
      imageId: null,
      teamId: null,
      isPlaceholder: true,
      canNavigate: false,
    }
  }

  return {
    name: realTeamName ?? slotName,
    imageId: team?.imageId ?? null,
    teamId,
    isPlaceholder: false,
    canNavigate: Boolean(teamId),
  }
}

/**
 * The visiting "team" object a GAME CARD should render.
 *
 * Cards resolve their visitor as `visitingTeam || visitingTeamSlot || {}` and
 * then read `.name` off the result, falling back to a literal 'TBD'. A
 * placeholder game satisfies none of those -- `visitingTeam` is null and there
 * is no slot -- so the card printed 'TBD' instead of the name the admin typed.
 *
 * Returning a team-SHAPED object keeps that existing `.name` read working
 * rather than forcing every card to branch. `id` is deliberately absent: cards
 * gate team navigation on it, and a placeholder has no team page to open.
 *
 * Returns null when this is not a placeholder game, so callers keep their own
 * slot/empty fallback untouched.
 */
export const resolvePlaceholderVisitorTeam = (
  game: VisitingTeamDisplaySource | null | undefined
): { name: string; name_full: string; is_placeholder_visitor: true } | null => {
  if (!hasPlaceholderVisitor(game)) {
    return null
  }
  const name = readPlaceholderVisitorName(game) ?? ''
  return { name, name_full: name, is_placeholder_visitor: true }
}
